import { NavLink, Outlet, useNavigate } from "react-router-dom";
import YasamaruHelp from "@/components/YasamaruHelp";
import { InquiryDialog } from "@/components/InquiryDialog";
import SubscriptionGate from "@/components/SubscriptionGate";
import { Home, CalendarDays, MessageCircle, LogOut, Settings, LifeBuoy, HelpCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const items = [
  { to: "/staff", label: "ホーム", icon: Home, end: true, testId: "nav-staff-home" },
  { to: "/staff/request", label: "希望提出", icon: CalendarDays, testId: "nav-staff-request" },
  { to: "/staff/my-shifts", label: "シフト", icon: CalendarDays, testId: "nav-staff-myshifts" },
  { to: "/staff/messages", label: "メッセージ", icon: MessageCircle, testId: "nav-staff-messages" },
  { to: "/staff/setup", label: "設定", icon: Settings, testId: "nav-staff-setup" },
];

export default function StaffLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    const fetch = () => api.get("/messages/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
    fetch();
    const id = setInterval(fetch, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <SubscriptionGate user={user}>
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-blue-600 flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-base font-bold text-slate-900 leading-tight">シフト管理</h1>
            <p className="text-xs text-slate-500 leading-tight">{user?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate("/staff/help")}
            data-testid="open-help-staff"
            className="text-slate-500 hover:text-blue-600 p-2"
            aria-label="使い方ガイド"
            title="使い方ガイド"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
          <button
            onClick={() => setInquiryOpen(true)}
            data-testid="open-inquiry-staff"
            className="text-slate-500 hover:text-blue-600 p-2"
            aria-label="お問い合わせ"
            title="お問い合わせ"
          >
            <LifeBuoy className="w-5 h-5" />
          </button>
          <button
            onClick={logout}
            data-testid="staff-logout-button"
            className="text-slate-500 hover:text-slate-700 p-2"
            aria-label="ログアウト"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 pb-20 overflow-y-auto">
        <Outlet />
      </main>

      <YasamaruHelp />
      <InquiryDialog open={inquiryOpen} onOpenChange={setInquiryOpen} />

      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex justify-around items-stretch z-20">
        {items.map((it) => {
          const Icon = it.icon;
          const isMessages = it.to === "/staff/messages";
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testId}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center py-2 min-h-[56px] text-xs ${
                  isActive ? "text-blue-600" : "text-slate-500"
                }`
              }
            >
              <div className="relative">
                <Icon className="w-6 h-6" />
                {isMessages && unread > 0 && (
                  <span
                    data-testid="staff-unread-badge"
                    className="absolute -top-1 -right-2 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                  >
                    {unread}
                  </span>
                )}
              </div>
              <span className="mt-0.5">{it.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
    </SubscriptionGate>
  );
}
